/**
 * Team, Verify a certificate (plan docs/plans/lms-plan.md 10.4, 10.2, 10.1,
 * 13 tab 6; Appendix A GET /admin/certificates/verify?code=; 18.19, 18.21;
 * R24; WP-45).
 *
 * Replaces WP-03's placeholder in place: tabs/team/subviews.ts imports this
 * file's DEFAULT export (no props), mounted at /lms/team/verify/* (18.3).
 *
 * An admin types the code printed on a certificate (LMS-YYYY-NNNNNN; the
 * number grows past six digits, 18.19) and sees what it certifies as the
 * certificate row recorded it (holder, course, version, completed on, valid
 * until) and its status now: valid, expired, or superseded (a newer
 * completion replaced it; superseded wins over expired, 10.1). A code whose
 * holder the admin does not reach answers the same "not found" as an unknown
 * code (18.19); every lookup is audited on the server.
 */
import { useState } from 'react';
import { BadgeCheck, Loader2, Search } from 'lucide-react';
import { fieldCls, primaryBtnCls } from '../../../../ui/styles';
import { apiErrorMessage } from '../../../api/client';
import { CERTIFICATE_CODE_RE, normalizeCertificateCode, verifyCertificate } from '../../../api/team';
import type { CertificateVerifyStatus, VerifiedCertificate } from '../../../api/team';
import { Pill } from '../../../components/StatusPill';
import type { PillTone } from '../../../components/StatusPill';
import { cardCls, ErrorNote, formatDay, labelCls, metaCls, microLabelCls } from '../../../components/pickers/shared';

export const VERIFY_STATUS: Record<CertificateVerifyStatus, { word: string; tone: PillTone; hint: string }> = {
  valid: { word: 'Valid', tone: 'green', hint: "The training this certificate records is current." },
  expired: { word: 'Expired', tone: 'red', hint: "The certificate's validity has ended." },
  superseded: { word: 'Superseded', tone: 'gray', hint: "A newer completion of this course replaced the one this certificate records." },
};

export function VerifyView() {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<VerifiedCertificate | null>(null);
  const normalized = normalizeCertificateCode(code);
  const wellFormed = CERTIFICATE_CODE_RE.test(normalized);

  const submit = async () => {
    if (busy) return;
    if (!wellFormed) {
      setError('A certificate code reads LMS-YYYY-NNNNNN, for example LMS-2026-000123.');
      setResult(null);
      return;
    }
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      setResult(await verifyCertificate(normalized));
    } catch (err) {
      setError(apiErrorMessage(err, 'The certificate could not be looked up. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  const status = result ? VERIFY_STATUS[result.status] ?? { word: result.status, tone: 'gray' as PillTone, hint: '' } : null;

  return (
    <div className="max-w-2xl space-y-4" data-testid="lms-verify">
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <div className="min-w-0 flex-1">
          <label className={labelCls} htmlFor="verify-code">
            Certificate code
          </label>
          <input
            id="verify-code"
            className={`${fieldCls} min-h-[44px] w-full font-mono sm:min-h-0`}
            placeholder="LMS-2026-000123"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            autoComplete="off"
            spellCheck={false}
            data-testid="verify-code"
          />
        </div>
        <button type="submit" className={primaryBtnCls} disabled={busy || !code.trim()} data-testid="verify-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Search className="h-4 w-4" aria-hidden="true" />}
          Verify
        </button>
      </form>

      <div aria-live="polite">
        {error ? <ErrorNote testId="verify-error">{error}</ErrorNote> : null}
        {result && status ? (
          <section className={cardCls} data-testid="verify-result" aria-labelledby="verify-result-title">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 id="verify-result-title" className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
                <BadgeCheck className="h-5 w-5 text-primary-600 dark:text-primary-400" aria-hidden="true" />
                <span className="font-mono">{result.certificate.code}</span>
              </h2>
              <Pill tone={status.tone} data-testid="verify-status">
                {status.word}
              </Pill>
            </div>
            <p className={`${metaCls} mt-1`}>{status.hint}</p>
            <dl className="mt-3 grid grid-cols-1 gap-x-4 gap-y-2 text-sm sm:grid-cols-2">
              <div>
                <dt className={microLabelCls}>Holder</dt>
                <dd className="text-gray-900 dark:text-gray-100" data-testid="verify-holder">
                  {result.holder.full_name ?? 'Unnamed person'}
                  {result.holder.email ? <span className={`${metaCls} block`}>{result.holder.email}</span> : null}
                </dd>
              </div>
              <div>
                <dt className={microLabelCls}>Course</dt>
                <dd className="text-gray-900 dark:text-gray-100" data-testid="verify-course">
                  {`${result.course.title}, version ${result.course.version_no}`}
                </dd>
              </div>
              <div>
                <dt className={microLabelCls}>Completed</dt>
                <dd className="text-gray-900 dark:text-gray-100">{formatDay(result.completed_at)}</dd>
              </div>
              <div>
                <dt className={microLabelCls}>Valid until</dt>
                <dd className="text-gray-900 dark:text-gray-100">{result.valid_until ? formatDay(result.valid_until) : 'Does not expire'}</dd>
              </div>
              <div>
                <dt className={microLabelCls}>Issued</dt>
                <dd className="text-gray-900 dark:text-gray-100">{formatDay(result.certificate.issued_at)}</dd>
              </div>
              {result.superseded_at ? (
                <div>
                  <dt className={microLabelCls}>Superseded</dt>
                  <dd className="text-gray-900 dark:text-gray-100" data-testid="verify-superseded-at">
                    {formatDay(result.superseded_at)}
                  </dd>
                </div>
              ) : null}
            </dl>
          </section>
        ) : null}
      </div>
    </div>
  );
}

export default VerifyView;
